import ApiUtil from "./apiUtils";
import { REQUEST_TIMEOUT } from "../config";
import { ApiResponse, IMiddleware, IRequestData, RequestMethod } from "./types";
import userService from "./user.service";

type InitConfig = {
  baseUrl?: string;
  defaultHeaders?: Record<string, string>;
  defaultConfig: Record<string, string>;
};

class Client extends ApiUtil {
  private createMiddlewareInstances: () => Array<IMiddleware>;
  private initConfig: InitConfig;

  // Main method that calls the fetch
  private async call(url: string, requestData: IRequestData): ApiResponse {
    // Add default data to requestData object
    this.addDefaultValues(
      requestData,
      url,
      this.initConfig.baseUrl,
      this.initConfig.defaultHeaders,
      this.initConfig.defaultConfig
    );

    const middlewares = this.createMiddlewareInstances();

    // Call the getRequest method of all the middlewares
    middlewares.forEach((middleware) => {
      if (middleware.getRequestData) {
        middleware.getRequestData(requestData);
      }
    });

    const controller = new AbortController();
    const { signal } = controller;
    setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    try {
      const { url, params, body, config, headers, method } = requestData;
      const requestBody = body && this.formatRequestData(body);
      // Call the fetch method
      const response = await fetch(ApiUtil.buildUrlWithParams(url as string, params), {
        ...config,
        signal,
        body: requestBody,
        headers,
        method,
      });
      if (response.ok) {
        return this.handleResponse(requestData, response, middlewares);
      }
      return this.handleError(requestData, response, middlewares);
    } catch (err) {
      return this.handleError(requestData, err as TypeError, middlewares);
    }
  }

  private handleResponse = async (
    request: IRequestData,
    response: Response,
    middlewares: Array<IMiddleware>
  ): ApiResponse => {
    // Call the getResponseData method of all the middlewares
    middlewares.forEach((middleware) => {
      if (middleware.getResponseData) {
        middleware.getResponseData(request, response);
      }
    });

    // Return raw response
    if (request.isRawResponse) {
      return {
        response,
        error: null,
      };
    }

    const body = await this.parseResponse(response);
    return {
      response: body,
      error: null,
    };
  };

  private handleError = async (
    request: IRequestData,
    response: Response | TypeError,
    middlewares: Array<IMiddleware>
  ): ApiResponse => {
    // Call the getErrorData method of all the middlewares
    middlewares.forEach((middleware) => {
      if (middleware.getErrorData) {
        middleware.getErrorData(request, response);
      }
    });

    if (response instanceof TypeError || !response.status) {
      return this.formatErrorMessage();
    }

    try {
      const body = await this.parseResponse(response);

      if (response.status === 401) {
        try {
          await userService.logout();
        } catch (e) {
          // Proceed with redirect even if logout cleanup fails
        }
        window.location.href = "/login";
        return this.formatErrorMessage(body, response.status, "Unauthorized access");
      }

      // Rest of your error handling
      return this.formatErrorMessage(body, response.status);
    } catch (error) {
      // Fallback error handling
      return this.formatErrorMessage(null, null, "Failed to parse server response");
    }
  };

  public constructor(createMiddlewareInstances: () => Array<IMiddleware> = () => [], initConfig: InitConfig) {
    super();
    this.createMiddlewareInstances = createMiddlewareInstances;
    this.initConfig = initConfig;
  }

  public get(
    url: string,
    { params = {}, headers = {}, config = {}, isRawResponse = false, statusMessageConfig = {} }: IRequestData
  ): ApiResponse {
    return this.call(url, {
      method: RequestMethod.GET,
      params,
      headers,
      config,
      isRawResponse,
      statusMessageConfig,
    });
  }

  public post(
    url: string,
    { body, params = {}, headers = {}, config = {}, isRawResponse = false, statusMessageConfig = {} }: IRequestData
  ): ApiResponse {
    return this.call(url, {
      method: RequestMethod.POST,
      body,
      params,
      headers,
      config,
      isRawResponse,
      statusMessageConfig,
    });
  }

  public put(
    url: string,
    { body, params = {}, headers = {}, config = {}, isRawResponse = false, statusMessageConfig = {} }: IRequestData
  ): ApiResponse {
    return this.call(url, {
      method: RequestMethod.PUT,
      body,
      params,
      headers,
      config,
      isRawResponse,
      statusMessageConfig,
    });
  }

  public patch(
    url: string,
    { body, params = {}, headers = {}, config = {}, isRawResponse = false, statusMessageConfig = {} }: IRequestData
  ): ApiResponse {
    return this.call(url, {
      method: RequestMethod.PATCH,
      body,
      params,
      headers,
      config,
      isRawResponse,
      statusMessageConfig,
    });
  }

  public delete(
    url: string,
    { body, params = {}, headers = {}, config = {}, isRawResponse = false, statusMessageConfig = {} }: IRequestData
  ): ApiResponse {
    return this.call(url, {
      method: RequestMethod.DELETE,
      body,
      params,
      headers,
      config,
      isRawResponse,
      statusMessageConfig,
    });
  }
}

export default Client;
